import React from 'react';
import { useSelector } from 'react-redux';
import { debounce } from 'lodash';
import { navigate } from 'gatsby';

import DocumentIcon from '../assets/icons/pdf2.svg';
import WordDocumentIcon from '../assets/icons/word-icon.png';
import YoutubeLogo from '../assets/images/youtube_logo.png';
import LockSubIcon from '../assets/icons/sub-lock.svg';
import UnlockSubIcon from '../assets/icons/open-sub-lock.svg';
import Chevron from '../assets/icons/chevron.svg';
import { trackDocumentClick } from '@src/utils/trackDocumentClick';

function getColor(value) {
  const hue = (value * 120).toString(10);
  return `hsl(${hue},100%,50%)`;
}

const GRADES = ['A*', 'A', 'B', 'C', 'D', 'E', 'U'];

const CHANCES = [
  'Certain',
  'Highly Likely',
  'Likely',
  'Less Likely',
  'Rarely',
  'Almost Never',
  'Never',
];

const DIFFICULTIES = [
  'Easy',
  'Medium',
  'Hard',
  'Very Hard',
];

const MODULES = [
  'Pure Year 1',
  'Stats Year 1',
  'Mechs Year 1',
  'Pure Year 2',
  'Stats Year 2',
  'Mechs Year 2',
  'Pure Year 1 and 2',
  'Stats Year 1 and 2',
  'Mechs Year 1 and 2',
  'AS Core',
  'A Level Core',
  'FS1',
  "FM1",
]

const FeaturedDocumentCard = ({ title, fileUrl }) => {
  const icon = fileUrl.includes('.pdf')
    ? DocumentIcon
    : fileUrl.includes('.docx')
    ? WordDocumentIcon
    : null;
  return (
    <div className="featured-document-card">
      <p>{title}</p>
      {icon && (
        <>
          <img src={Chevron} className="chevron" alt="" />
          <div
            className="img-container"
            onClick={() => {
              trackDocumentClick(
                { name: title, fileUrl },
                { source: 'topic_table', outcome: 'opened', destinationUrl: fileUrl },
              );
              window.open(fileUrl, '_blank');
            }}
          >
            <img src={icon} alt="Document icon" />
          </div>
        </>
      )}
    </div>
  );
};

/**
 *
 * @param {{
 *  table: {
 *      columns: string[],
 *      data: Record<string, string>[]
 *  },
 * title: string,
 * description: string,
 * featuredDocuments: {
 *  title: string,
 *  fileUrl: string
 * }[]
 * }} param0
 * @returns
 */
export default function TopicTable({ table, title, description, featuredDocuments = [] }) {
  const [searchTerm, setSearchTerm] = React.useState('');
  const [filteredResults, setFilteredResults] = React.useState(table.data);
  const [selectedSortIndex, setSelectedSortIndex] = React.useState(-1);

  const [isShowing, setIsShowing] = React.useState(true);

  const user = useSelector((state) => state.user.data);
  const isSubscribed = user?.subscribed ?? false;

  const renderRow = (row) => {
    return (
      <tr>
        {table.columns
          .filter((header) => header.toLowerCase() !== 'subscription')
          .map((header) => (
            <td>{renderCell(row[header], header, row)}</td>
          ))}
      </tr>
    );
  };

  const renderCell = (value, header, row) => {
    if (header === 'Topic') {
      return <div>{value}</div>;
    } else if (header === 'Grade') {
      const overlappingGrade = value.includes('/');
      if (overlappingGrade) {
        const grades = value.split('/').map((v) => v.trim());
        const fromGrade = grades[0];
        const toGrade = grades[1];
        const isLetterBased = /[a-zA-Z]/.test(fromGrade);
        let fromGradeIndex;
        let toGradeIndex;
        if (isLetterBased) {
          fromGradeIndex = 9 - GRADES.indexOf(fromGrade);
          toGradeIndex = 9 - GRADES.indexOf(toGrade);
        } else {
          fromGradeIndex = parseInt(fromGrade);
          toGradeIndex = parseInt(toGrade);
        }
        return (
          <div
            className={`grade grade-gradient from-grade-${fromGradeIndex} to-grade-${toGradeIndex} grade-small`}
          >
            {value}
          </div>
        );
      } else {
        const isLetterBased = /[a-zA-Z]/.test(value);
        let grade;
        if (isLetterBased) {
          grade = 9 - GRADES.indexOf(value);
        } else {
          const gradeMatches = value.match(/(\d+)/);
          grade = gradeMatches ? gradeMatches[0] : null;
        }
        return <div className={`grade grade-${grade}`}>{value}</div>;
      }
    } else if (header.includes('%')) {
      const numberMatches = value.match(/(\d+)/);
      const percentage = numberMatches ? numberMatches[0] : 0;
      const decimal = (percentage / 100).toFixed(2);
      return (
        <div
          className="percentage"
          style={{
            backgroundColor: decimal == '0.00' ? 'black' : getColor(decimal),
            color: decimal == '0.00' ? 'white' : 'black',
          }}
        >
          {value}
        </div>
      );
    } else if (value.includes('.pdf') || value.includes('/embedded')) {
      const checkForSpaces = value.includes(" ");
      let isASubscriptionDocument = row?.['Subscription']?.toLowerCase() === 'true';
      console.log({checkForSpaces})
      if (checkForSpaces) {
        const splitValue = value.split(" ");
        console.log({splitValue})
        if (splitValue.length === 2) {
          if (splitValue[1] === "true") {
            isASubscriptionDocument = true;
          }
          if (splitValue[1] === "false") {
            isASubscriptionDocument = false;
          }
        }
      }
      return (
        <div
          className="document-icon-container"
          onClick={() => {
            const locked = isASubscriptionDocument && !isSubscribed;
            trackDocumentClick(
              { fileUrl: value, isSubscription: isASubscriptionDocument },
              {
                source: 'topic_table',
                outcome: locked ? 'subscribe' : 'opened',
                isSubscribed,
                destinationUrl: locked ? '/subscriptions' : value,
              },
            );
            if (locked) {
              navigate('/subscriptions');
            } else {
              window.open(value, '_blank');
            }
          }}
        >
          {isASubscriptionDocument && (
            <img
              src={isSubscribed ? UnlockSubIcon : LockSubIcon}
              className="category-option-image lock-icon"
              style={{ cursor: 'default' }}
            />
          )}
          <img
            src={DocumentIcon}
            className="category-option-image doc-icon"
            alt="Category option"
          />
        </div>
      );
    } else if (value.includes('.docx')) {
      const checkForSpaces = value.includes("");
      let isASubscriptionDocument = row?.['Subscription']?.toLowerCase() === 'true';
      if (checkForSpaces) {
        const splitValue = value.split(" ");
        if (splitValue.length === 2) {
          if (splitValue[1] === "true") {
            isASubscriptionDocument = true;
          }
          if (splitValue[1] === "true") {
            isASubscriptionDocument = false;
          }
        }
      }
      return (
        <div
          className="document-icon-container"
          onClick={() => {
            const locked = isASubscriptionDocument && !isSubscribed;
            trackDocumentClick(
              { fileUrl: value, isSubscription: isASubscriptionDocument },
              {
                source: 'topic_table',
                outcome: locked ? 'subscribe' : 'opened',
                isSubscribed,
                destinationUrl: locked ? '/subscriptions' : value,
              },
            );
            if (locked) {
              navigate('/subscriptions');
            } else {
              window.open(value, '_blank');
            }
          }}
        >
          {isASubscriptionDocument && (
            <img
              src={isSubscribed ? UnlockSubIcon : LockSubIcon}
              className="category-option-image lock-icon"
              style={{ cursor: 'default' }}
            />
          )}
          <img
            src={WordDocumentIcon}
            className="category-option-image doc-icon"
            alt="Category option"
          />
        </div>
      );
    } else if (value.includes('youtube.com')) {
      return (
        <div className="document-icon-container">
          <img
            src={YoutubeLogo}
            onClick={() => window.open(value, '_blank')}
            className="category-option-image"
            alt="Category option"
          />
        </div>
      );
    } else if (CHANCES.some((chance) => chance.toLowerCase() == value.toLowerCase())) {
      return (
        <div className={`chance chance-${value.split(' ').join('-').toLowerCase()}`}>{value}</div>
      );
    } else if (DIFFICULTIES.some((difficulty) => difficulty.toLowerCase() == value.toLowerCase())) {
      return (
        <div className={`difficulty difficulty-${value.split(' ').join('-').toLowerCase()}`}>{value}</div>
      );
    } else if (MODULES.some((module) => module.toLowerCase() == value.toLowerCase())) {
      return (
        <div className={`module module-${value.split(' ').join('-').toLowerCase()}`}>{value}</div>
      );
    }  else {
      return <div className="text-centered bold">Coming soon!</div>;
    }
  };

  const debouncedSearch = debounce((searchTerm) => {
    if (!searchTerm.length) {
      setFilteredResults(table.data);
    } else {
      setFilteredResults(
        table.data.filter((row) => row['Topic'].toLowerCase().includes(searchTerm.toLowerCase()) || row['Module']?.toLowerCase().includes(searchTerm.toLowerCase())),
      );
    }
    if (selectedSortIndex !== -1) {
      handleSort(sortableColumns[selectedSortIndex]);
    }
  }, 500);

  const chanceBasedColumnIndices = table.data.length
    ? Object.values(table?.data?.[0])?.reduce((acc, cell, index) => {
        if (CHANCES.some((chance) => chance.toLowerCase() == cell.toLowerCase())) acc.push(index);
        return acc;
      }, [])
    : [];

  const hasGradeColumn = table.data.length ? table.data[0]["Grade"] !== undefined : false;
  
  const moduleBasedColumnIndices = table.data.length
    ? Object.values(table?.data?.[0])?.reduce((acc, cell, index) => {
        if (MODULES.some((chance) => chance.toLowerCase() == cell.toLowerCase())) acc.push(index);
        return acc;
      }, [])
    : [];


  const difficultyBasedColumnIndices = table.data.length
    ? Object.values(table?.data?.[0])?.reduce((acc, cell, index) => {
        if (DIFFICULTIES.some((chance) => chance.toLowerCase() == cell.toLowerCase())) acc.push(index);
        return acc;
      }, [])
    : [];

  const sortableColumns = [
    ...(hasGradeColumn ? [{ name: 'Grade', type: 'grade' }] : []),
    ...table.columns
      .filter((_, index) => chanceBasedColumnIndices.includes(index))
      .map((header) => ({
        name: header,
        type: 'chance',
      })),
    ...table.columns
      .filter((_, index) => moduleBasedColumnIndices.includes(index))
      .map((header) => ({
        name: header,
        type: 'module',
      })),
    ...table.columns
      .filter((_, index) => difficultyBasedColumnIndices.includes(index))
      .map((header) => ({
        name: header,
        type: 'difficulty',
      })),
    ...table.columns
      .filter((header) => header.includes('%'))
      .map((header) => ({
        name: header,
        type: 'percentage',
      })),
  ];

  const handleSort = ({ name, type }) => {
    if (type === 'grade') {
      const isLetterBased = /[a-zA-Z]/.test(table.data[0]['Grade']);
      if (isLetterBased) {
        setFilteredResults((prev) => {
          const copy = [...prev];
          copy.sort((a, b) => {
            const gradeAIndex = GRADES.indexOf(a['Grade']);
            const gradeBIndex = GRADES.indexOf(b['Grade']);
            return gradeAIndex - gradeBIndex;
          });
          return copy;
        });
      } else {
        setFilteredResults((prev) => {
          const copy = [...prev];
          copy.sort((a, b) => {
            const aMatch = +a['Grade'].match(/\d+/)[0];
            const bMatch = +b['Grade'].match(/\d+/)[0];
            return bMatch - aMatch;
          });

          return copy;
        });
      }
    } else if (type === 'percentage') {
      setFilteredResults((prev) => {
        const copy = [...prev];
        copy.sort((a, b) => {
          const aMatch = +a[name].match(/\d+/)[0];
          const bMatch = +b[name].match(/\d+/)[0];
          return bMatch - aMatch;
        });

        return copy;
      });
    } else if (type === 'chance') {
      setFilteredResults((prev) => {
        const copy = [...prev];
        copy.sort((a, b) => {
          const aChanceIndex = CHANCES.findIndex(
            (chance) => chance.toLowerCase() === a[name].toLowerCase(),
          );
          const bChanceIndex = CHANCES.findIndex(
            (chance) => chance.toLowerCase() === b[name].toLowerCase(),
          );
          return aChanceIndex - bChanceIndex;
        });
        return copy;
      });
    } else if (type === 'module') {
      setFilteredResults((prev) => {
        const copy = [...prev];
        copy.sort((a, b) => {
          const aIndex = MODULES.indexOf(a[name]);
          const bIndex = MODULES.indexOf(b[name]);
          return aIndex - bIndex;
        });
        return copy;
      });
    } else if (type === 'difficulty') {
      setFilteredResults((prev) => {
        const copy = [...prev];
        copy.sort((a, b) => {
          const aIndex = DIFFICULTIES.indexOf(a[name]);
          const bIndex = DIFFICULTIES.indexOf(b[name]);
          return aIndex - bIndex;
        });
        return copy;
      });
    } 
  };

  return (
    <div className="topic-table-container">
      <h3 id={title.split(' ').join('-').toLowerCase()}>{title}</h3>
      <button className="button ml-0 my-1" onClick={() => setIsShowing((prev) => !prev)}>
        {isShowing ? 'Hide' : 'Show'} topic table
      </button>

      {isShowing && (
        <>
          {description && <div dangerouslySetInnerHTML={{ __html: description }} />}
          {featuredDocuments.length ? (
            <div className="featured-doc-row">
              {featuredDocuments.map((featuredDoc) => (
                <FeaturedDocumentCard {...featuredDoc} />
              ))}
            </div>
          ) : null}
          <div className="filter-container">
            <input
              type="text"
              placeholder="Search for a topic"
              className="input"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                debouncedSearch(e.target.value);
              }}
            />
            <div className="button-container">
              <p>Order by</p>
              {sortableColumns.map((sortingOption, i) => (
                <button
                  className="button"
                  onClick={() => {
                    setSelectedSortIndex(i);
                    handleSort(sortingOption);
                  }}
                >
                  {sortingOption.name}
                </button>
              ))}
            </div>
          </div>
          <div className="table-container">
            <table className="table topic-table">
              <thead>
                <tr
                  style={{
                    textAlign: 'center',
                    backgroundColor: '#fd99c5',
                    border: '1px solid black',
                  }}
                >
                  {table.columns
                    .filter((header) => header.toLowerCase() !== 'subscription')
                    .map((header) => (
                      <th
                        className="p-1"
                        style={{ textAlign: 'center', border: '1px solid black' }}
                      >
                        {header}
                      </th>
                    ))}
                </tr>
              </thead>
              <tbody>{filteredResults.map((row) => renderRow(row))}</tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
